import { useEffect, useState, useMemo } from 'react';
import { 
  Search, 
  UserCheck, 
  ExternalLink,
  MapPin,
  X,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useStore } from '../lib/store';
import { cn } from '../lib/utils';
import ClientDetail from '../components/ClientDetail';

type SortField = 'business_name' | 'activity' | 'location' | 'plan' | 'site_status' | 'paid_at';
type SortDir = 'asc' | 'desc';

const SITE_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'En attente', color: 'text-gray-600', bg: 'bg-gray-100' },
  building: { label: 'Construction', color: 'text-blue-700', bg: 'bg-blue-100' },
  waiting_client: { label: 'Attente client', color: 'text-amber-700', bg: 'bg-amber-100' },
  client_responded: { label: 'Client a répondu', color: 'text-indigo-700', bg: 'bg-indigo-100' },
  built: { label: '⚠️ À valider', color: 'text-orange-700', bg: 'bg-orange-100' },
  built_auto: { label: '⚠️ À valider', color: 'text-orange-700', bg: 'bg-orange-100' },
  live: { label: 'En ligne ✅', color: 'text-green-700', bg: 'bg-green-100' },
  error: { label: 'Erreur', color: 'text-red-700', bg: 'bg-red-100' },
};

function formatDateFr(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  } catch { return '—'; }
}

export default function Clients() {
  const { clients, fetchClients, loading } = useStore();
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('paid_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  useEffect(() => {
    fetchClients();
  }, []);
  
  const filteredClients = useMemo(() => {
    let result = [...clients];
    
    // Search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(c => 
        c.business_name?.toLowerCase().includes(q) ||
        c.contact_name?.toLowerCase().includes(q) ||
        c.contact_email?.toLowerCase().includes(q) ||
        c.activity?.toLowerCase().includes(q) ||
        c.location?.toLowerCase().includes(q)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(c => c.site_status === statusFilter);
    }
    
    // Sort
    result.sort((a: any, b: any) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      
      switch (sortField) {
        case 'business_name':
          aVal = (a.business_name || '').toLowerCase();
          bVal = (b.business_name || '').toLowerCase();
          break;
        case 'activity':
          aVal = (a.activity || '').toLowerCase();
          bVal = (b.activity || '').toLowerCase();
          break;
        case 'location':
          aVal = (a.location || '').toLowerCase();
          bVal = (b.location || '').toLowerCase();
          break;
        case 'plan':
          aVal = (a.plan || '').toLowerCase();
          bVal = (b.plan || '').toLowerCase();
          break;
        case 'site_status':
          aVal = (a.site_status || '').toLowerCase();
          bVal = (b.site_status || '').toLowerCase();
          break;
        case 'paid_at':
          aVal = new Date(a.paid_at || 0).getTime();
          bVal = new Date(b.paid_at || 0).getTime();
          break;
      }
      
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [clients, search, sortField, sortDir, statusFilter]);
  
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 text-gray-300" />;
    return sortDir === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-primary-500" /> 
      : <ChevronDown className="w-4 h-4 text-primary-500" />;
  };
  
  // Stats par statut
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    clients.forEach((c: any) => {
      const s = c.site_status || 'pending';
      stats[s] = (stats[s] || 0) + 1;
    });
    return stats;
  }, [clients]);

  // Revenue mensuel
  const monthlyRevenue = useMemo(() => {
    return clients.reduce((sum: number, c: any) => sum + (c.monthly_amount || 0), 0);
  }, [clients]);
  
  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <UserCheck className="w-6 h-6 text-green-600" />
          Clients
        </h1>
        <p className="text-gray-500 mt-1">
          {clients.length} client{clients.length > 1 ? 's' : ''} · Revenue mensuel : <span className="font-semibold text-green-600">{monthlyRevenue}€/mois</span>
        </p>
      </div>
      
      {/* Quick Stats */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <button
          onClick={() => setStatusFilter('all')}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all whitespace-nowrap',
            statusFilter === 'all' 
              ? 'bg-gray-900 text-white border-gray-900' 
              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
          )}
        >
          Tous
          <span className={cn(
            'px-2 py-0.5 rounded-full text-xs',
            statusFilter === 'all' ? 'bg-white/20' : 'bg-gray-100'
          )}>
            {clients.length}
          </span>
        </button>
        
        {Object.entries(SITE_STATUS_CONFIG).map(([key, config]) => {
          const count = statusStats[key] || 0;
          if (count === 0) return null;
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all whitespace-nowrap',
                statusFilter === key
                  ? `${config.bg} ${config.color} border-current`
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              )}
            >
              {config.label}
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs',
                statusFilter === key ? 'bg-white/60' : 'bg-gray-100'
              )}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
      
      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, activité, ville..."
            className="w-full h-10 pl-10 pr-4 bg-white border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-shadow"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80">
                <th 
                  onClick={() => handleSort('business_name')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                >
                  <div className="flex items-center gap-2">
                    Client
                    <SortIcon field="business_name" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('activity')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                >
                  <div className="flex items-center gap-2">
                    Activité
                    <SortIcon field="activity" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('plan')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none hidden md:table-cell"
                >
                  <div className="flex items-center gap-2">
                    Plan
                    <SortIcon field="plan" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('site_status')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                >
                  <div className="flex items-center gap-2">
                    Statut site
                    <SortIcon field="site_status" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('paid_at')}
                  className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none hidden lg:table-cell"
                >
                  <div className="flex items-center gap-2">
                    Date commande
                    <SortIcon field="paid_at" />
                  </div>
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" /><div className="space-y-2"><div className="h-4 w-32 bg-gray-200 rounded animate-pulse" /><div className="h-3 w-24 bg-gray-100 rounded animate-pulse" /></div></div></td>
                    <td className="px-4 py-4"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-4 py-4 hidden md:table-cell"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-4 py-4"><div className="h-6 w-20 bg-gray-200 rounded-full animate-pulse" /></td>
                    <td className="px-4 py-4 hidden lg:table-cell"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-4 py-4"><div className="h-8 w-8 bg-gray-200 rounded animate-pulse ml-auto" /></td>
                  </tr>
                ))
              ) : filteredClients.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <UserCheck className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-900 font-medium mb-1">Aucun client trouvé</p>
                      <p className="text-sm text-gray-500">
                        {search ? "Essayez avec d'autres termes de recherche" : 'Les clients payés apparaîtront ici'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredClients.map((client: any) => {
                  const statusConfig = SITE_STATUS_CONFIG[client.site_status || ''] || SITE_STATUS_CONFIG.pending;
                  return (
                    <tr 
                      key={client.id}
                      className="group hover:bg-gray-50/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedClient(client)}
                    >
                      {/* Client info */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center text-green-700 font-medium text-sm flex-shrink-0">
                            {(client.business_name || '?')[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {client.business_name || 'Sans nom'}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {client.contact_name}
                              {client.location && (
                                <span className="inline-flex items-center gap-0.5 ml-2">
                                  <MapPin className="w-3 h-3" />
                                  {client.location}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </td>
                      
                      {/* Activity */}
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 truncate block max-w-[180px]">
                          {client.activity || '—'}
                        </span>
                      </td>
                      
                      {/* Plan */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div>
                          <span className="text-sm text-gray-700">
                            {client.plan === 'oneshot' ? 'Clé en main' : 'SaaS'}
                          </span>
                          {client.monthly_amount > 0 && (
                            <p className="text-xs text-green-600 font-medium">{client.monthly_amount}€/mois</p>
                          )}
                        </div>
                      </td>
                      
                      {/* Site Status */}
                      <td className="px-4 py-3">
                        <span className={cn(
                          'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
                          statusConfig.bg,
                          statusConfig.color
                        )}>
                          {statusConfig.label}
                        </span>
                      </td>
                      
                      {/* Date */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-sm text-gray-500">
                          {formatDateFr(client.paid_at)}
                        </span>
                      </td>
                      
                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedClient(client); }}
                            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                            title="Voir les détails"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {client.site_url && (
                            <a
                              href={client.site_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              title="Voir le site"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        {filteredClients.length > 0 && (
          <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100 text-sm text-gray-500">
            {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} 
            {search || statusFilter !== 'all' ? ' (filtré)' : ''}
          </div>
        )}
      </div>
      
      {/* Client Detail Panel */}
      {selectedClient && (
        <ClientDetail 
          client={selectedClient} 
          onClose={() => setSelectedClient(null)} 
        />
      )}
    </div>
  );
}
